import { CONTENT_TYPE_PRODUCT_KEYS } from '@confluence/content-types-bridge-types/entry-points/contentTypeBridgeSharedTypes.ts';
import type {
	ContentTypeCommand as ContentTypeCommandBase,
	ContentTypeCommandType,
	ContentTypeProductKey,
} from '@confluence/content-types-bridge-types/entry-points/contentTypeBridgeSharedTypes.ts';

export type ContentTypeEventHandler = () => void;

/**
 * The full internal command shape: the sender-agnostic base union plus
 * `source` injected automatically by `sendCommand`.
 */
export type ContentTypeCommand = ContentTypeCommandBase & {
	source: ContentTypeProductKey;
};

export type ContentTypeCommandHandler = (message: ContentTypeCommand) => void;

/**
 * Adds a `source` field to a command while preserving its discriminated union member
 */
const withSource = <T extends ContentTypeCommandBase>(
	command: T,
	source: ContentTypeProductKey,
): T & { source: ContentTypeProductKey } => ({
	...command,
	source,
});

// Shared service that currently holds methods for whiteboards version history
// This can be extended to support databases related work
export class ContentTypeEventService {
	private eventHandlers: Map<string, ContentTypeEventHandler>;

	// Will eventually completely replace V1 event handlers
	private eventHandlersByProduct: Map<
		ContentTypeProductKey,
		Map<string, ContentTypeCommandHandler>
	>;

	constructor() {
		this.eventHandlers = new Map();

		this.eventHandlersByProduct = new Map();
		CONTENT_TYPE_PRODUCT_KEYS.forEach((key) => {
			this.eventHandlersByProduct.set(key, new Map());
		});
	}

	dispose() {
		// Using clear here instead of re-assignment to a new map to save memory.
		this.eventHandlers.clear();
		this.eventHandlersByProduct.clear();
	}

	/**
	 * This is called in Whiteboards to register the any event callbacks.
	 * Confluence FE can then use any of these events to trigger the whiteboard events.
	 */
	registerEventHandler(event: string, handler: ContentTypeEventHandler): void {
		this.eventHandlers.set(event, handler);
	}

	/**
	 * Setup function to be called by a product to register event callbacks. When you use this, you can
	 * listen to certain events from other products and NOTIFY other products about certain events
	 */
	mountEventService(source: ContentTypeProductKey) {
		/**
		 * Expected usage in downstream product:
		 * const eventService = contentTypesEventService.mountEventService('whiteboards');
		 * eventService.registerHandler('copy', (message) => {
		 *   // Handle the copy event
		 *   console.log('Whiteboards has been notified about a copy', message);
		 * });
		 *
		 * Invoking a command from another product (e.g., Confluence):
		 * const confluenceEventService = contentTypesEventService.mountEventService('confluence');
		 * confluenceEventService.sendCommand({
		 *  type: 'copy',
		 * });
		 */
		return {
			/**
			 * Called by whatever product to mount a listener that will only trigger when OTHER products
			 * invoke a command that matches the ContentTypeCommandType.
			 */
			registerHandler: (event: ContentTypeCommandType, handler: ContentTypeCommandHandler) => {
				this.eventHandlersByProduct.get(source)?.set(event, handler);
			},

			/**
			 * Called by whatever product to unregister a listener that was previously registered
			 */
			unregisterHandler: (event: ContentTypeCommandType) => {
				this.eventHandlersByProduct.get(source)?.delete(event);
			},

			/**
			 * Check if an event has any handlers registered to it
			 * @param event
			 */
			hasEventHandler: (event: ContentTypeCommandType) => {
				return this.eventHandlersByProduct.get(source)?.has(event);
			},

			/**
			 * Trigger an event that OTHER products will receive
			 */
			sendCommand: (message: ContentTypeCommandBase) => {
				if (!message || !message.type) {
					return;
				}

				// Call all other sources and notify them that we have triggered an event
				CONTENT_TYPE_PRODUCT_KEYS.forEach((productKey) => {
					// Exclude self. NO SELF TRIGGERING!
					if (productKey === source) {
						return;
					}

					this.eventHandlersByProduct.get(productKey)?.get(message.type)?.(
						withSource(message, source),
					);
				});
			},
		};
	}

	/**
	 * This is called in Whiteboards to unregister the any event callbacks.
	 */
	unregisterEventHandler(event: string): void {
		this.eventHandlers.delete(event);
	}

	/**
	 * Check if an event has any handlers registered to it
	 * @param event
	 */
	hasEventHandler(event: string): boolean {
		return this.eventHandlers.has(event);
	}

	/**
	 * Trigger an event in Whiteboards.
	 */
	triggerEvent(event: string): void {
		this.eventHandlers.get(event)?.();
	}
}
